<template>
  <!-- Header for monitoring Inspection name -->
  <HeaderBar
    v-if="!loading"
    :modifiers="['light']"
  >
    <div>
      <strong class="typo-title capitalize">
        {{ `${$t('plants.inspection')} #${renderShortId(inspection.id)}` }}
      </strong>
      <div
        v-if="inspection.id"
        class="header-bar__sub-title typo-tiny"
      >
        {{ inspectedBy }} | {{ formatDate(inspection.date) }}
      </div>
    </div>
    <template #right>
      <DetailHeaderPrEng v-if="inspection.professional_engineer" />
      <DetailHeaderButton v-if="inspection.policy.edit && inspection.status !== 'approved'">
        <PencilIcon
          aria-label="edit-inspection"
          class="icon"
          @click="editInspectionModal"
        />
      </DetailHeaderButton>
      <DetailHeaderButton
        v-if="inspection.policy.destroy && inspection.status !== 'approved'"
        id="delete-inspection"
      >
        <TrashIcon
          aria-label="delete-inspection"
          class="icon"
          @click="confirmDeleteAction"
        />
      </DetailHeaderButton>
      <!-- TODO: Add route name to new issue form -->
      <NewIssueButton
        v-if="inspection.policy.create && inspection.status !== 'approved'"
        class="hide-for-large"
        :hasIcon="true"
        :hasText="false"
        :routeName="newIssueRoute.name"
      />
      <button
        v-if="bulkImport && inspection.policy.create && inspection.status !== 'approved'"
        :aria-label="$t('issues.bulk_import')"
        class="button button--no-margin-left button--margin-right hide-for-large"
        type="button"
        @click="$emit('toggleUploadModal')"
      >
        <UseSvg
          class="icon icon--small button__icon"
          name="upload"
        />
        <span class="button__label show-for-large">
          {{ $t('issues.bulk_import') }}
        </span>
      </button>
      <DropdownLink
        v-if="inspection.status === 'approved' && inspection.download_url"
        :items="[
          { label: $t('general.pdf'), path: `${inspection.download_url}?report_type=pdf`, target: '_blank' },
          { label: $t('general.csv'), path: `${inspection.download_url}?report_type=csv` }
        ]"
        :label="$t('general.download_report')"
        :modifiers="['margin-right', (inspection.professional_engineer && 'left-border')]"
      />
      <Select
        class="select--input select--elongated"
        :items="inspectionSelectOptions"
        :labelIcons="statusIcons"
        :modelValue="inspection.status"
        :placeholder="inspectionStatus"
        @update:model-value="updateStatus"
      />
      <div
        v-if="inspection.policy.edit"
        class="header-bar__right-edit dropdown hide-for-large"
      >
        <Popper
          v-model:open="menuOpen"
          placement="bottom-end"
        >
          <template #trigger>
            <div class="dropdown__trigger">
              <EllipsisIcon class="icon" />
            </div>
          </template>
          <div class="menu menu--dropdown">
            <ul class="menu__items">
              <li
                v-if="inspection.policy.edit"
                class="menu-item typo-list-item menu-item--dropdown"
              >
                <button
                  class="menu-item__link"
                  type="button"
                  @click="editInspectionModal"
                >
                  {{ $t('general.edit') }}
                </button>
              </li>
              <li
                v-if="inspection.policy.destroy"
                class="menu-item typo-list-item menu-item--dropdown"
              >
                <button
                  class="menu-item__link"
                  type="button"
                  @click="toggleDialog"
                >
                  {{ $t('general.delete') }}
                </button>
              </li>
            </ul>
          </div>
        </Popper>
      </div>
    </template>
  </HeaderBar>
  <!-- Condition and rating bar -->
  <HeaderBar
    v-if="!loading && shaftStatus.isShaft && !shaftStatus.isShaftV2"
    class="hide-for-small show-for-medium show-for-large"
  >
    <div class="header-bar__title-container-details header-bar__line">
      <div class="header-bar__title-container-details-count">
        <p
          v-if="inspection.status !== 'approved'"
          class="typo-small typo-ignore header-bar__info--white"
        >
          {{ $t('inspections.provisional_inspection_overview') }}
        </p>
        <p
          v-else
          class="typo-small typo-ignore header-bar__info--white"
        >
          {{ $t('inspections.approved_inspection_overview') }}
        </p>
      </div>
      <div class="header-bar__title-container-details-info">
        <p
          class="typo-small typo-ignore header-bar__info--white"
        >
          {{ $t('inspections.condition_rating') }}
          <span :class="`issue-card__risk issue-ratings__condition--${conditionLevel} margin-left center`">
            {{ conditionRating }}
          </span>
        </p>
        <p
          class="typo-small typo-ignore header-bar__info--white"
        >
          {{ $t('inspections.risk_rating') }}
          <span :class="`issue-card__risk issue-ratings__risk--${riskLevel} margin-left center`">
            {{ riskRating }}
          </span>
        </p>
      </div>
    </div>
  </HeaderBar>
  <!-- Issues bar -->
  <HeaderBar v-if="!loading">
    <div class="header-bar__title-container-details">
      <div class="header-bar__title-container-details-count">
        <Select
          v-if="(!shaftStatus.isShaft || shaftStatus.isShaftV2) && issues.length > 0 && filterByOptions"
          v-model="filterBy"
          :items="filterByOptions"
          :placeholder="$t('general.sort_by')"
          @update:model-value="$emit('filterBy', filterBy)"
        />
        <p
          v-if="shaftStatus.isShaft && !shaftStatus.isShaftV2"
          class="typo-small typo-ignore header-bar__info"
        >
          <em v-if="inspection.issues">
            {{
              pluralize(
                inspection.issues.length,
                $t('activerecord.models.plants/issue.one'),
                $t('activerecord.models.plants/issue.other')
              )
            }}
          </em>
        </p>
      </div>
      <div class="header-bar__title-container-details-info show-for-large">
        <p
          v-if="inspection.status === 'approved'"
          class="typo-small typo-ignore header-bar__info"
        >
          {{ $t('inspections.approved_by') }}
          <em>{{ approvedBy }}</em>
        </p>
        <!-- TODO: Add route name to new issue form -->
        <NewIssueButton
          v-if="inspection?.policy?.create && inspection?.status !== 'approved'"
          class="show-for-large"
          :hasIcon="true"
          :routeName="newIssueRoute?.name"
        />
        <button
          v-if="bulkImport && inspection.policy.create && inspection.status !== 'approved'"
          :aria-label="$t('issues.bulk_import')"
          class="button button--no-margin-left button--margin-right"
          type="button"
          @click="$emit('toggleUploadModal')"
        >
          <UseSvg
            class="icon icon--small button__icon"
            name="upload"
          />
          <span class="button__label show-for-large">
            {{ $t('issues.bulk_import') }}
          </span>
        </button>
      </div>
    </div>
  </HeaderBar>

  <ShaftInspectionForm
    v-model:modalIsOpen="modalIsOpen"
    :initialInspection="inspection"
    @save="saveInspection"
  />

  <Dialog
    v-if="dialogIsOpen"
    :message="$t('inspections.delete_confirmation_message')"
    :title="$t('inspections.delete_confirmation_title')"
    @dismiss-dialog="toggleDialog"
  >
    <template #actions>
      <button
        aria-label="delete-inspection"
        class="button button--inverse"
        type="button"
        @click="$emit('deleteInspection')"
      >
        {{ $t('general.delete') }}
      </button>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, computed } from 'vue';
import I18n from 'i18n-js';

// Utils
import { formatDate } from '@/functions/date_format_helpers';
import { renderShortId } from '@/functions/utils.js';
import truncateNumber from '@/functions/simm/truncate_number';
import pluralize from '@/functions/pluralize';

// Components
import DetailHeaderButton from '@/components/inspections/detail/detail_header_button.vue';
import DetailHeaderPrEng from '@/components/inspections/detail/detail_header_preng.vue';
import Dialog from '@/components/generic/dialog.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Popper from '@/components/generic/popper.vue';
import Select from '@/components/form/select.vue';
import DropdownLink from '@/components/generic/download_link.vue';
import NewIssueButton from '@/components/inspections/inspection_new_issue_button.vue';
import ShaftInspectionForm from '@/components/inspections/inspection_form.vue';
import UseSvg from '@/components/generic/use_svg.vue';

// Icons
import EllipsisIcon from '@/images/vertical-ellipsis.svg';
import PencilIcon from '@/images/pencil-24.svg';
import TrashIcon from '@/images/trash-24.svg';

const emit = defineEmits(['filterBy', 'updateStatus', 'updateInspection', 'deleteInspection', 'toggleUploadModal']);
const props = defineProps({
  loading: { type: Boolean, default: false },
  inspection: { type: Object, required: true, default: () => {} },
  currentOperation: { type: Object, required: true, default: () => {} },
  newIssueRoute: { type: Object, required: true, default: () => {} },
  issues: { type: Array, required: true, default: () => [] },
  filterByOptions: { type: Array, required: true, default: () => [] },
  riskLevel: { type: String, required: false, default: '' },
  conditionLevel: { type: String, required: false, default: '' },
  shaftStatus: { type: Object, required: false, default: () => ({ isShaft: false, isShaftV2: false }) },
  bulkImport: { type: Boolean, default: false },
});

const menuOpen = ref(false);
const filterBy = ref(null);
const dialogIsOpen = ref(false);
const confirmDelete = ref(false);
const modalIsOpen = ref(false);

const inspectionSelectIcons = {
  in_progress: 'viewfinder_dot_16',
  awaiting_approval: 'dashed_circle_16',
  approved: 'checkmark',
};

const statusIcons = {
  in_progress: { name: 'viewfinder_dot_16', class: 'icon icon--small' },
  awaiting_approval: {
    name: 'dashed_circle_16',
    class: 'icon icon--small',
  },
  approved: { name: 'checkmark', class: 'icon icon--small' },
};

const riskRating = computed(() => {
  return truncateNumber(props.inspection?.risk_rating);
});

const conditionRating = computed(() => {
  return truncateNumber(props.inspection?.condition_rating);
});

/**
 * Returns I18n text for inspection statuses
 */
const inspectionStatus = computed(() => {
  return I18n.t(`inspections.statuses.${props.inspection.status}.name`);
});

const inspectedBy = computed(() => {
  return props.inspection?.inspected_by?.full_name || '';
});

const approvedBy = computed(() => {
  return props.inspection?.approved_by?.full_name || '';
});

const inspectionSelectOptions = computed(() =>
  ['in_progress', 'awaiting_approval', 'approved'].map((status) => ({
    label: I18n.t(`inspections.statuses.${status}.name`),
    value: status,
    disabled:
      props.loading ||
      (!props.inspection.policy[`change_to_${status}`] &&
        props.inspection.status !== status),
    details: {
      description: I18n.t(`inspections.statuses.${status}.description`),
      name: inspectionSelectIcons[status],
    },
  }))
);

/**
 * Opens edit modal
 */
function editInspectionModal() {
  props.inspection.trusted_user = props.currentOperation?.trusted_user;
  menuOpen.value = false;
  modalIsOpen.value = true;
}

function toggleDialog() {
  dialogIsOpen.value = !dialogIsOpen.value;
  menuOpen.value = false;
}

function confirmDeleteAction() {
  confirmDelete.value = true;
  toggleDialog();
}

function updateStatus(status) {
  emit('updateStatus', status);
}

async function saveInspection(data) {
  emit('updateInspection', data);
}
</script>
